/**
 * Whistle Sync Service — cloud-first architecture
 *
 * hydrateFromCloud() — fetches all management data from Supabase into Dexie cache.
 *   Called on login and when coming back online. Also propagates remote deletes.
 *
 * flushStatEntries() — pushes unsynced local stat entries to Supabase.
 *   Stat entries are the only local-first data (must work offline during live games).
 */

import { db } from './db'
import { supabase } from './supabase'
import {
  rowToTeam, rowToSeason, rowToPlayer, rowToGame, rowToRoster,
  rowToLineup, rowToSlot, rowToEvent, rowToOpponent, rowToDrillTemplate,
  rowToStatEntry, statEntryToRow, gameToRow,
} from './mappers'
import type { Game, TeamMemberRole } from '@/types'

// ── Pending game updates ───────────────────────────────────────────────────────
// Game updates that failed to reach Supabase (e.g. network down during end-game).
// Stored in memory; flushed at the start of every hydrateFromCloud (push-before-pull).

type PendingGameUpdate = { gameId: string; data: Partial<Game> }
const pendingGameUpdates: PendingGameUpdate[] = []

export function queueGameUpdate(gameId: string, data: Partial<Game>): void {
  const existing = pendingGameUpdates.find(e => e.gameId === gameId)
  if (existing) Object.assign(existing.data, data)
  else pendingGameUpdates.push({ gameId, data })
}

async function flushPendingGameUpdates(): Promise<void> {
  if (pendingGameUpdates.length === 0) return
  const session = (await supabase.auth.getSession()).data.session
  if (!session || !navigator.onLine) return

  for (const pending of [...pendingGameUpdates]) {
    try {
      const existing = await db.games.get(pending.gameId)
      if (!existing) { pendingGameUpdates.splice(pendingGameUpdates.indexOf(pending), 1); continue }
      const updated: Game = { ...existing, ...pending.data, updatedAt: new Date().toISOString() }
      const { error } = await supabase.from('games').upsert(gameToRow(updated))
      if (!error) {
        await db.games.put(updated)
        pendingGameUpdates.splice(pendingGameUpdates.indexOf(pending), 1)
      }
    } catch { /* retry on next sync */ }
  }
}

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'skipped'
export interface SyncResult {
  status: SyncStatus
  reason?: string
  lastSyncedAt?: string
}

let syncInProgress = false

/**
 * Hydrate the local Dexie cache from Supabase (full replace).
 * Also propagates remote deletes by removing Dexie records
 * that no longer exist in Supabase.
 */
export async function hydrateFromCloud(): Promise<SyncResult> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return { status: 'skipped', reason: 'unauthenticated' }
  const userId = session.user.id

  if (!navigator.onLine) return { status: 'skipped', reason: 'offline' }
  if (syncInProgress) return { status: 'skipped', reason: 'already running' }
  syncInProgress = true

  try {
    // Push any locally-saved game updates before pulling remote data
    await flushPendingGameUpdates()

    // ── Step 1: Resolve accessible team IDs ───────────────────────────────────
    const [membershipsRes, ownTeamsRes] = await Promise.all([
      supabase.from('team_members').select('team_id, role').eq('user_id', userId),
      supabase.from('teams').select('id').eq('user_id', userId),
    ])
    const roleMap = Object.fromEntries(
      (membershipsRes.data ?? []).map(r => [r.team_id as string, r.role as TeamMemberRole])
    )
    const allTeamIds = [...new Set([
      ...(membershipsRes.data ?? []).map(r => r.team_id as string),
      ...(ownTeamsRes.data ?? []).map(r => r.id as string),
    ])]

    // ── Step 2: Fetch all management data from Supabase ───────────────────────
    const [teamsRes, playersRes] = await Promise.all([
      allTeamIds.length > 0
        ? supabase.from('teams').select('*').in('id', allTeamIds)
        : Promise.resolve({ data: [] as Record<string, unknown>[], error: null }),
      // No user_id filter — RLS returns own players + players on any team the user is a member of
      supabase.from('players').select('*'),
    ])
    if (teamsRes.error) throw new Error(`teams: ${teamsRes.error.message}`)
    if (playersRes.error) throw new Error(`players: ${playersRes.error.message}`)

    const teams = (teamsRes.data ?? []).map(r => ({ ...rowToTeam(r), myRole: roleMap[r.id as string] }))
    const players = (playersRes.data ?? []).map(rowToPlayer)

    const seasonsRes = allTeamIds.length > 0
      ? await supabase.from('seasons').select('*').in('team_id', allTeamIds)
      : { data: [] as Record<string, unknown>[], error: null }
    if (seasonsRes.error) throw new Error(`seasons: ${seasonsRes.error.message}`)
    const seasons = (seasonsRes.data ?? []).map(rowToSeason)
    const allSeasonIds = seasons.map(s => s.id)

    const [rostersRes, opponentsRes, drillsRes, eventsRes] = await Promise.all([
      allSeasonIds.length > 0
        ? supabase.from('season_rosters').select('*').in('season_id', allSeasonIds)
        : Promise.resolve({ data: [] as Record<string, unknown>[], error: null }),
      allTeamIds.length > 0
        ? supabase.from('opponents').select('*').in('team_id', allTeamIds)
        : Promise.resolve({ data: [] as Record<string, unknown>[], error: null }),
      allTeamIds.length > 0
        ? supabase.from('drill_templates').select('*').in('team_id', allTeamIds)
        : Promise.resolve({ data: [] as Record<string, unknown>[], error: null }),
      allSeasonIds.length > 0
        ? supabase.from('events').select('*').in('season_id', allSeasonIds)
        : Promise.resolve({ data: [] as Record<string, unknown>[], error: null }),
    ])
    if (rostersRes.error) throw new Error(`season_rosters: ${rostersRes.error.message}`)
    if (opponentsRes.error) throw new Error(`opponents: ${opponentsRes.error.message}`)
    if (drillsRes.error) throw new Error(`drill_templates: ${drillsRes.error.message}`)
    if (eventsRes.error) throw new Error(`events: ${eventsRes.error.message}`)

    const gamesRes = allSeasonIds.length > 0
      ? await supabase.from('games').select('*').in('season_id', allSeasonIds)
      : { data: [] as Record<string, unknown>[], error: null }
    if (gamesRes.error) throw new Error(`games: ${gamesRes.error.message}`)
    const games = (gamesRes.data ?? []).map(rowToGame)
    const allGameIds = games.map(g => g.id)

    const lineupsRes = allGameIds.length > 0
      ? await supabase.from('lineups').select('*').in('game_id', allGameIds)
      : { data: [] as Record<string, unknown>[], error: null }
    if (lineupsRes.error) throw new Error(`lineups: ${lineupsRes.error.message}`)
    const lineups = (lineupsRes.data ?? []).map(rowToLineup)
    const allLineupIds = lineups.map(l => l.id)

    const slotsRes = allLineupIds.length > 0
      ? await supabase.from('lineup_slots').select('*').in('lineup_id', allLineupIds)
      : { data: [] as Record<string, unknown>[], error: null }
    if (slotsRes.error) throw new Error(`lineup_slots: ${slotsRes.error.message}`)

    // Fetch synced stat entries from Supabase (entries written on other devices)
    const statEntriesRes = allGameIds.length > 0
      ? await supabase.from('stat_entries').select('*').in('game_id', allGameIds)
      : { data: [] as Record<string, unknown>[], error: null }
    if (statEntriesRes.error) throw new Error(`stat_entries: ${statEntriesRes.error.message}`)
    const remoteStatEntries = (statEntriesRes.data ?? []).map(rowToStatEntry)

    const rosters = (rostersRes.data ?? []).map(rowToRoster)
    const opponents = (opponentsRes.data ?? []).map(rowToOpponent)
    const drillTemplates = (drillsRes.data ?? []).map(rowToDrillTemplate)
    const events = (eventsRes.data ?? []).map(rowToEvent)
    const slots = (slotsRes.data ?? []).map(rowToSlot)

    // ── Step 3: Bulk put into Dexie cache ──────────────────────────────────────
    await Promise.all([
      db.teams.bulkPut(teams),
      db.players.bulkPut(players),
      db.seasons.bulkPut(seasons),
      db.games.bulkPut(games),
      db.lineups.bulkPut(lineups),
      db.opponents.bulkPut(opponents),
      db.drillTemplates.bulkPut(drillTemplates),
      db.events.bulkPut(events),
      db.seasonRosters.bulkPut(rosters),
      db.lineupSlots.bulkPut(slots),
      // Mark remote entries as synced so flushStatEntries won't re-upload them
      db.statEntries.bulkPut(remoteStatEntries.map(e => ({ ...e, syncedAt: e.syncedAt ?? new Date().toISOString() }))),
    ])

    // ── Step 4: Propagate remote deletes ───────────────────────────────────────
    // For simple-key tables: remove local records that no longer exist in Supabase
    const teamIdSet       = new Set(teams.map(t => t.id))
    const playerIdSet     = new Set(players.map(p => p.id))
    const seasonIdSet     = new Set(seasons.map(s => s.id))
    const gameIdSet       = new Set(games.map(g => g.id))
    const lineupIdSet     = new Set(lineups.map(l => l.id))
    const opponentIdSet   = new Set(opponents.map(o => o.id))
    const drillIdSet      = new Set(drillTemplates.map(d => d.id))
    const eventIdSet      = new Set(events.map(e => e.id))
    const remoteStatIdSet = new Set(remoteStatEntries.map(e => e.id))

    const [localTeamKeys, localPlayerKeys, localSeasonKeys, localGameKeys,
           localLineupKeys, localOpponentKeys, localDrillKeys, localEventKeys,
           localStatKeys] = await Promise.all([
      db.teams.toCollection().primaryKeys() as Promise<string[]>,
      db.players.toCollection().primaryKeys() as Promise<string[]>,
      db.seasons.toCollection().primaryKeys() as Promise<string[]>,
      db.games.toCollection().primaryKeys() as Promise<string[]>,
      db.lineups.toCollection().primaryKeys() as Promise<string[]>,
      db.opponents.toCollection().primaryKeys() as Promise<string[]>,
      db.drillTemplates.toCollection().primaryKeys() as Promise<string[]>,
      db.events.toCollection().primaryKeys() as Promise<string[]>,
      db.statEntries.toCollection().primaryKeys() as Promise<string[]>,
    ])

    await Promise.all([
      db.teams.bulkDelete(localTeamKeys.filter(id => !teamIdSet.has(id))),
      db.players.bulkDelete(localPlayerKeys.filter(id => !playerIdSet.has(id))),
      db.seasons.bulkDelete(localSeasonKeys.filter(id => !seasonIdSet.has(id))),
      db.games.bulkDelete(localGameKeys.filter(id => !gameIdSet.has(id))),
      db.lineups.bulkDelete(localLineupKeys.filter(id => !lineupIdSet.has(id))),
      db.opponents.bulkDelete(localOpponentKeys.filter(id => !opponentIdSet.has(id))),
      db.drillTemplates.bulkDelete(localDrillKeys.filter(id => !drillIdSet.has(id))),
      db.events.bulkDelete(localEventKeys.filter(id => !eventIdSet.has(id))),
    ])

    // Compound-key tables: remove local entries not in the fetched set.
    // Scope to ALL local entries (not just fetched IDs) so deletes propagate
    // even for seasons/lineups that were deleted remotely.
    const fetchedRosterKeys = new Set(rosters.map(r => `${r.seasonId}|${r.playerId}`))
    const allLocalRosters = await db.seasonRosters.toArray()
    for (const r of allLocalRosters) {
      if (!fetchedRosterKeys.has(`${r.seasonId}|${r.playerId}`)) {
        await db.seasonRosters.where('[seasonId+playerId]').equals([r.seasonId, r.playerId]).delete()
      }
    }

    const fetchedSlotKeys = new Set(slots.map(s => `${s.lineupId}|${s.playerId}`))
    const allLocalSlots = await db.lineupSlots.toArray()
    for (const s of allLocalSlots) {
      if (!fetchedSlotKeys.has(`${s.lineupId}|${s.playerId}`)) {
        await db.lineupSlots.where('[lineupId+playerId]').equals([s.lineupId, s.playerId]).delete()
      }
    }

    // Remove synced stat entries that were deleted remotely.
    // Only touch entries for games we fetched — preserve unsynced local entries from live games.
    if (allGameIds.length > 0) {
      const gameIdSetForStats = new Set(allGameIds)
      const staleStatIds = localStatKeys.filter(id => !remoteStatIdSet.has(id))
      if (staleStatIds.length > 0) {
        const staleEntries = await db.statEntries.where('id').anyOf(staleStatIds).toArray()
        const toDelete = staleEntries
          .filter(e => e.syncedAt && gameIdSetForStats.has(e.gameId))
          .map(e => e.id)
        if (toDelete.length > 0) await db.statEntries.bulkDelete(toDelete)
      }
    }

    await db.syncMeta.clear()

    return { status: 'idle', lastSyncedAt: new Date().toISOString() }
  } catch (e) {
    return { status: 'error', reason: (e as Error).message }
  } finally {
    syncInProgress = false
  }
}

/**
 * Push unsynced local stat entries to Supabase.
 * Stat entries are written locally during live games (offline-safe)
 * and flushed after the game ends or when back online.
 */
export async function flushStatEntries(): Promise<SyncResult> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return { status: 'skipped', reason: 'unauthenticated' }
  if (!navigator.onLine) return { status: 'skipped', reason: 'offline' }

  try {
    const unsynced = await db.statEntries.filter(e => !e.syncedAt).toArray()
    if (!unsynced.length) return { status: 'idle', lastSyncedAt: new Date().toISOString() }

    const { error } = await supabase
      .from('stat_entries')
      .upsert(unsynced.map(statEntryToRow), { onConflict: 'id' })
    if (error) throw new Error(`stat_entries: ${error.message}`)

    const now = new Date().toISOString()
    await Promise.all(unsynced.map(e => db.statEntries.update(e.id, { syncedAt: now })))

    return { status: 'idle', lastSyncedAt: now }
  } catch (e) {
    return { status: 'error', reason: (e as Error).message }
  }
}
